import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, FileText, Download } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";

export default function Files() {
  const [files, setFiles] = useState(null);
  const [downloads, setDownloads] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/staff/files")
      .then(({ data }) => setFiles(data.files))
      .catch((e) => toast.error(formatApiError(e, "Could not load files")));
    api.get("/staff/downloads")
      .then(({ data }) => setDownloads(data.downloads))
      .catch(() => {});
  }, []);

  const download = async (file) => {
    setBusy(file.key);
    try {
      const { data } = await api.get(`/staff/files/${file.key}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e, "Could not download that file")); }
    finally { setBusy(""); }
  };

  return (
    <div>
      <Seo title="Files — EI Battery Workspace" />
      <span className="ei-eyebrow">Reference</span>
      <h1 className="mt-1 text-2xl sm:text-3xl font-bold text-ei-navy">Files</h1>
      <p className="mt-2 max-w-3xl text-sm text-ei-graphite/70">
        Internal reference documents. These are only available to signed-in staff — they are not published on the
        public site and have no shareable link.
      </p>

      <div className="mt-6 space-y-4" data-testid="staff-files-list">
        {!files ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-ei-navy" /></div>
        ) : files.map((file) => (
          <article key={file.key} className="rounded-md border border-ei-mist bg-white p-5 sm:p-6" data-testid={`staff-file-${file.key}`}>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex gap-4">
                <FileText className="mt-0.5 h-6 w-6 flex-shrink-0 text-ei-golddark" />
                <div>
                  <h2 className="font-display text-base font-bold text-ei-navy">{file.title}</h2>
                  <p className="mt-1 max-w-xl text-sm leading-relaxed text-ei-graphite/75">{file.description}</p>
                  <p className="mt-2 text-xs text-ei-graphite/55">
                    Updated {file.updated}{file.size_kb ? ` · PDF · ${file.size_kb} KB` : ""}
                  </p>
                </div>
              </div>
              <button type="button" onClick={() => download(file)} disabled={!file.available || busy === file.key}
                className="inline-flex items-center gap-2 rounded-sm bg-ei-navy px-5 py-3 font-display text-sm font-bold text-ei-white transition-colors hover:bg-ei-navy/90 disabled:opacity-50"
                data-testid={`staff-file-download-${file.key}`}>
                {busy === file.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {file.available ? "Download" : "Not available"}
              </button>
            </div>
          </article>
        ))}
      </div>

      <h2 className="mt-12 font-display text-lg font-bold text-ei-navy">Public document downloads</h2>
      <p className="mt-1 text-sm text-ei-graphite/70">How many times each document on the website has been downloaded. No personal details are recorded.</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-3" data-testid="staff-download-counts">
        {downloads.map((row) => (
          <div key={row.key} className="rounded-md border border-ei-mist bg-white p-5" data-testid={`download-count-${row.key}`}>
            <div className="font-display text-3xl font-bold tabular-nums text-ei-navy">{row.count}</div>
            <div className="mt-1 font-display text-sm font-bold text-ei-navy">{row.label}</div>
            <div className="mt-1 text-xs text-ei-graphite/55">
              {row.last_downloaded_at ? `Last ${new Date(row.last_downloaded_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}` : "No downloads yet"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
